import { useCallback, useEffect, useState } from "react";
import { DEFAULT_TAB, pathForTab, tabForPath } from "../lib/desk-tabs";
import type { DeskTab } from "../lib/desk-tabs";

/**
 * Which of the desk's four views is showing, and how to move between them (`APP-016`).
 *
 * The view is read from `window.location.pathname`, so a founder who lands on `/results`
 * directly gets Results — the Cloudflare asset config serves this shell for every path
 * (`wrangler.jsonc`, `not_found_handling: single-page-application`).
 *
 * Moving between views uses `history.pushState` rather than a navigation, for one reason
 * that is about correctness and not about polish: `DeskScreen` holds five independent reads,
 * and a real navigation would tear them all down and re-issue them on every tab click. The
 * auction poll would restart, a panel that had failed would lose its error, and a founder
 * would watch four skeletons to reach a list they had already loaded.
 *
 * `popstate` is what makes the back button work; without it `pushState` would change the
 * address bar and strand the view.
 */
export function useDeskTab(): { tab: DeskTab; goTo: (tab: DeskTab) => void } {
  const [tab, setTab] = useState<DeskTab>(() =>
    typeof window === "undefined" ? DEFAULT_TAB : tabForPath(window.location.pathname),
  );

  useEffect(() => {
    const onPopState = () => {
      setTab(tabForPath(window.location.pathname));
    };
    window.addEventListener("popstate", onPopState);
    return () => {
      window.removeEventListener("popstate", onPopState);
    };
  }, []);

  const goTo = useCallback((next: DeskTab) => {
    const path = pathForTab(next);
    // The query and fragment are preserved: `?state=` from a sign-in round trip and a
    // fragment a founder was linked to both belong to the desk, not to the view.
    if (window.location.pathname !== path) {
      window.history.pushState(null, "", `${path}${window.location.search}${window.location.hash}`);
    }
    setTab(next);
  }, []);

  return { tab, goTo };
}
